import React, { useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { EditorContent } from '@tiptap/react';
import { Box } from '@mui/material';

import { getLiveEditor } from '@/shared/utils/tiptapEditor';
import { isFeatureEnabled } from '@/shared/libs/featureFlags';

type PageSize = 'letter' | 'a4';

export type ZoomSetting = 'fit-width' | number;

export const DOCUMENT_ZOOM_PRESETS = [50, 75, 90, 100, 125, 150, 200] as const;

const MIN_ZOOM_PERCENT = 25;
const MAX_ZOOM_PERCENT = 400;
const CANVAS_SIDE_PADDING_PX = 32;

// 96 CSS-dpi conversion of the inch-based page widths used in PAGE_DIMENSIONS.
const PAGE_WIDTH_PX: Record<PageSize, number> = {
  letter: 8.5 * 96,
  a4: 8.27 * 96,
};

interface TiptapDocumentCanvasProps {
  editor: Editor | null;
  pageSize?: PageSize;
  useStandardTable?: boolean;
  zoomSetting?: ZoomSetting;
}

const PAGE_DIMENSIONS: Record<PageSize, { width: string; minHeight: string }> = {
  letter: { width: '8.5in', minHeight: '11in' },
  a4: { width: '8.27in', minHeight: '11.69in' },
};

const TiptapDocumentCanvas: React.FC<TiptapDocumentCanvasProps> = ({
  editor,
  pageSize = 'letter',
  useStandardTable = false,
  zoomSetting = 100,
}) => {
  const liveEditor = getLiveEditor(editor);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const [containerWidthPx, setContainerWidthPx] = useState<number | null>(null);

  useEffect(() => {
    const node = canvasRef.current;
    if (!node || zoomSetting !== 'fit-width' || typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setContainerWidthPx(entry.contentRect.width);
      }
    });
    observer.observe(node);
    setContainerWidthPx(node.clientWidth);
    return () => observer.disconnect();
  }, [zoomSetting]);

  if (!liveEditor) {
    return null;
  }

  const dimensions = PAGE_DIMENSIONS[pageSize];

  const resolvedZoom =
    zoomSetting === 'fit-width'
      ? clampZoom(
          containerWidthPx
            ? ((containerWidthPx - CANVAS_SIDE_PADDING_PX * 2) / PAGE_WIDTH_PX[pageSize]) * 100
            : 100
        )
      : clampZoom(zoomSetting);

  const useCssZoom = isFeatureEnabled('documentZoomUsesCssZoomProperty');
  const zoomFactor = resolvedZoom / 100;
  const zoomStyle = useCssZoom
    ? { zoom: zoomFactor }
    : {
        transform: `scale(${zoomFactor})`,
        transformOrigin: 'top center',
        // `transform` doesn't change layout; this widens the layout box so the
        // outer scroll container reserves space matching the visually scaled
        // content at any zoom.
        width: `${100 / zoomFactor}%`,
      };

  return (
    <Box
      ref={canvasRef}
      className="document-editor-canvas"
      sx={{
        backgroundColor: '#f1f3f4', // Light gray background like Google Docs
        minHeight: '100%',
        height: '100%',
        padding: '2rem 0',
        overflow: 'auto',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box className="document-editor-zoom-wrapper" sx={zoomStyle}>
        <Box
          sx={{
            fontFamily: 'Arial, sans-serif',
            fontSize: '11pt',
            lineHeight: 1.15,
            '& .ProseMirror': {
              outline: 'none',
              minHeight: dimensions.minHeight,
              fontFamily: 'inherit',
              fontSize: 'inherit',
              lineHeight: 'inherit',
              color: '#000000',
            },
            '& .ProseMirror:not(.rm-with-pagination)': {
              width: dimensions.width,
              maxWidth: dimensions.width,
              padding: '1in',
              margin: '0 auto',
              backgroundColor: '#ffffff',
              boxShadow: '0 1px 3px rgba(60,64,67,0.15), 0 1px 2px rgba(60,64,67,0.3)',
              boxSizing: 'border-box',
            },
            '& .ProseMirror.rm-with-pagination': {
              backgroundColor: '#ffffff',
              boxShadow: '0 1px 3px rgba(60,64,67,0.15), 0 1px 2px rgba(60,64,67,0.3)',
              border: '1px solid #dadce0',
              borderRadius: 0,
              margin: '0 auto',
              minHeight: dimensions.minHeight,
            },
            '& .ProseMirror.rm-with-pagination .rm-page-break': {
              position: 'relative',
              breakBefore: 'page',
              pageBreakBefore: 'always',
            },
            '& .ProseMirror.rm-with-pagination .rm-pagination-gap': {
              backgroundColor: '#f1f3f4 !important',
              borderTopColor: '#e0e0e0 !important',
              borderBottomColor: '#e0e0e0 !important',
              height: '20px !important',
            },
            '& .ProseMirror.rm-with-pagination .rm-page-header, & .ProseMirror.rm-with-pagination .rm-page-footer':
              {
                color: '#666',
                cursor: 'default !important',
                fontSize: '10pt',
              },
            '& .ProseMirror.rm-with-pagination .rm-page-header *, & .ProseMirror.rm-with-pagination .rm-page-footer *':
              {
                cursor: 'default !important',
              },
            '& .ProseMirror.rm-with-pagination .rm-page-break:first-of-type': {
              breakBefore: 'auto',
              pageBreakBefore: 'auto',
            },
            '& .ProseMirror p': {
              margin: '0 0 6pt 0',
              fontSize: '11pt',
              lineHeight: 1.15,
            },
            '& .ProseMirror h1': {
              fontSize: '20pt',
              fontWeight: 'normal',
              margin: '0 0 6pt 0',
              lineHeight: 1.15,
            },
            '& .ProseMirror h2': {
              fontSize: '16pt',
              fontWeight: 'normal',
              margin: '0 0 6pt 0',
              lineHeight: 1.15,
            },
            '& .ProseMirror h3': {
              fontSize: '14pt',
              fontWeight: 'normal',
              margin: '0 0 4pt 0',
              lineHeight: 1.15,
            },
            '& .ProseMirror h4': {
              fontSize: '12pt',
              fontWeight: 'normal',
              margin: '0 0 4pt 0',
              lineHeight: 1.15,
            },
            '& .ProseMirror h5': {
              fontSize: '11pt',
              fontWeight: 'normal',
              margin: '0 0 4pt 0',
              lineHeight: 1.15,
            },
            '& .ProseMirror h6': {
              fontSize: '11pt',
              fontWeight: 'normal',
              margin: '0 0 4pt 0',
              lineHeight: 1.15,
            },
            '& .ProseMirror ul, & .ProseMirror ol': {
              paddingLeft: '18pt',
              margin: '0 0 6pt 0',
            },
            // Markers only. This surface sets its own list padding and margins just
            // above, so it needs the browser's marker types back but not its box
            // model. Without these, Preflight's `list-style: none` strips every bullet
            // and number from documents, on screen and in printed output. The taskList
            // rule below resets `list-style` at higher specificity, so checklists are
            // unaffected. Written longhand to match this file, which is deliberately
            // independent of the shared authored-content contract.
            '& .ProseMirror ul': { listStyleType: 'disc' },
            '& .ProseMirror ol': { listStyleType: 'decimal' },
            '& .ProseMirror ul ul, & .ProseMirror ol ul': { listStyleType: 'circle' },
            '& .ProseMirror ol ol ul, & .ProseMirror ol ul ul, & .ProseMirror ul ol ul, & .ProseMirror ul ul ul':
              { listStyleType: 'square' },
            // Both are authorable here (link is enabled, horizontalRule is not
            // disabled) and neither had an explicit rule, so both rendered on browser
            // defaults that Preflight removes.
            '& .ProseMirror a': { color: 'LinkText', textDecoration: 'underline' },
            '& .ProseMirror hr': {
              border: 0,
              borderTop: '1px solid',
              borderColor: 'divider',
              margin: '0.5em auto',
            },
            '& .ProseMirror ul[data-type="taskList"]': {
              listStyle: 'none',
              paddingLeft: 0,
              margin: '0 0 6pt 0',
            },
            '& .ProseMirror ul[data-type="taskList"] ul[data-type="taskList"]': {
              margin: '4pt 0 0 24px',
            },
            '& .ProseMirror li': {
              margin: '0 0 6pt 0',
            },
            '& .ProseMirror li[data-type="taskItem"]': {
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              alignItems: 'start',
              width: '100%',
              margin: '0 0 4pt 0',
            },
            '& .ProseMirror li[data-type="taskItem"] > label': {
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '8px',
              marginTop: '2px',
              userSelect: 'none',
            },
            '& .ProseMirror li[data-type="taskItem"] > label > span': {
              position: 'relative',
              display: 'inline-block',
              width: 16,
              height: 16,
              border: '1.5px solid #8a8a8a',
              borderRadius: '2px',
              backgroundColor: '#ffffff',
              boxSizing: 'border-box',
              pointerEvents: 'none',
            },
            '& .ProseMirror li[data-type="taskItem"] > label > input[type="checkbox"]:checked + span::after':
              {
                content: '""',
                position: 'absolute',
                left: '4px',
                top: '1px',
                width: '5px',
                height: '9px',
                border: 'solid #6f6f6f',
                borderWidth: '0 2px 2px 0',
                transform: 'rotate(45deg)',
              },
            '& .ProseMirror li[data-type="taskItem"] > div': {
              minWidth: 0,
            },
            '& .ProseMirror li[data-type="taskItem"][data-checked="true"] > div > p': {
              textDecoration: 'line-through',
            },
            '& .ProseMirror li[data-type="taskItem"] > div > *:first-child': {
              marginTop: 0,
            },
            '& .ProseMirror li[data-type="taskItem"] > div > *:last-child': {
              marginBottom: 0,
            },
            '& .ProseMirror li[data-type="taskItem"] > div > p': {
              marginTop: '0.15em',
              marginBottom: '0.15em',
            },
            '& .ProseMirror li[data-type="taskItem"] > div ul[data-type="taskList"]': {
              margin: '4pt 0 0 0',
            },
            '& .ProseMirror li[data-type="taskItem"] input[type="checkbox"]': {
              position: 'absolute',
              opacity: 0,
              width: 16,
              height: 16,
              margin: 0,
              cursor: 'pointer',
            },
            '& .ProseMirror img': {
              maxWidth: '100%',
              height: 'auto',
              display: 'block',
              margin: '6pt 0',
            },
            '& .ProseMirror .image-plus-wrapper': {
              width: '100%',
              maxWidth: '100%',
              margin: '6pt 0',
            },
            '& .ProseMirror .document-image-placeholder-node': {
              margin: '6pt 0',
            },
            '& .ProseMirror .document-image-placeholder-node img': {
              margin: 0,
            },
            '& .ProseMirror .image-plus-container': {
              maxWidth: '100%',
              lineHeight: 0,
            },
            '& .ProseMirror .image-plus-img': {
              maxWidth: '100%',
              height: 'auto',
              display: 'block',
              margin: 0,
            },
            '& .ProseMirror .image-plus-shimmer': {
              borderRadius: '4px',
            },
            '@keyframes image-shimmer': {
              '0%': { backgroundPosition: '200% 0' },
              '100%': { backgroundPosition: '-200% 0' },
            },
            '& .ProseMirror .image-plus-container.image-plus-selected': {
              outline: '2px solid #1a73e8',
            },
            '& .ProseMirror .image-resize-handle': {
              display: 'none',
              position: 'absolute',
              width: 9,
              height: 9,
              background: '#fff',
              border: '1px solid #6C6C6C',
              borderRadius: '50%',
              zIndex: 10,
              touchAction: 'none',
            },
            '& .ProseMirror .image-plus-container.image-plus-selected .image-resize-handle': {
              display: 'block',
            },
            '& .ProseMirror .image-resize-handle[data-direction="top-left"]': {
              top: -5,
              left: -5,
              cursor: 'nwse-resize',
            },
            '& .ProseMirror .image-resize-handle[data-direction="top-right"]': {
              top: -5,
              right: -5,
              cursor: 'nesw-resize',
            },
            '& .ProseMirror .image-resize-handle[data-direction="bottom-left"]': {
              bottom: -5,
              left: -5,
              cursor: 'nesw-resize',
            },
            '& .ProseMirror .image-resize-handle[data-direction="bottom-right"]': {
              bottom: -5,
              right: -5,
              cursor: 'nwse-resize',
            },
            '& .ProseMirror table': {
              borderCollapse: 'collapse',
              width: '100%',
              maxWidth: '100%',
              tableLayout: 'fixed',
              margin: '6pt 0',
            },
            '& .ProseMirror th, & .ProseMirror td': {
              border: '1px solid #ddd',
              padding: '6px 8px',
              textAlign: 'left',
              verticalAlign: 'top',
              position: 'relative',
              boxSizing: 'border-box',
              whiteSpace: 'normal',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
              '& > *': {
                marginBottom: 0,
              },
            },
            '& .ProseMirror th': {
              backgroundColor: '#f2f2f2',
              fontWeight: 600,
            },
            // Selected cell — Google Docs uses a light blue tint with a blue border
            '& .ProseMirror .selectedCell::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'rgba(26, 115, 228, 0.12)',
              pointerEvents: 'none',
              zIndex: 2,
            },
            // Column resize handle — thin blue vertical bar on the right edge
            '& .ProseMirror .column-resize-handle': {
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: 3,
              background: '#1a73e8',
              pointerEvents: 'none',
              zIndex: 3,
            },
            // Column resize cursor — applied to the editor root during drag
            '& .ProseMirror.resize-cursor, & .ProseMirror.resize-cursor *': {
              cursor: 'col-resize !important',
            },
            // --- Pagination-aware table CSS (standard table mode) ---
            // PaginationPlus injects its own <style> with rules like:
            //   table tbody { display: table; max-height: 300px }
            //   table tbody > tr { display: table-row !important }
            // These trap rows inside a scrollable tbody instead of letting them
            // flow across pages. We MUST use !important on every override to win
            // the specificity battle against the library's injected stylesheet.
            ...(useStandardTable
              ? {
                  '& .ProseMirror.rm-with-pagination table': {
                    display: 'contents !important',
                  },
                  '& .ProseMirror.rm-with-pagination table tbody': {
                    display: 'contents !important',
                    maxHeight: 'none !important',
                    overflowY: 'visible !important',
                    width: '100%',
                  },
                  // Must counter PaginationPlus's `display: table-row !important`
                  '& .ProseMirror.rm-with-pagination table tbody > tr, & .ProseMirror.rm-with-pagination table > tr':
                    {
                      display: 'grid !important',
                      width: '100%',
                      position: 'relative',
                      boxSizing: 'border-box',
                    },
                  // Also dissolve any .table-row-group wrappers if present
                  '& .ProseMirror.rm-with-pagination .table-row-group': {
                    display: 'contents !important',
                    maxHeight: 'none !important',
                    overflowY: 'visible !important',
                  },
                  // Grid-column spans for merged cells (colspan) in grid-based rows.
                  // CSS grid doesn't natively interpret the HTML colspan attribute;
                  // we must map it explicitly. Covers colspan 2–10, which handles
                  // all practical table layouts.
                  ...Object.fromEntries(
                    Array.from({ length: 9 }, (_, i) => i + 2).map((n) => [
                      `& .ProseMirror.rm-with-pagination td[colspan="${n}"], & .ProseMirror.rm-with-pagination th[colspan="${n}"]`,
                      { gridColumn: `span ${n}` },
                    ])
                  ),
                  // Safety valve: prevent infinite pagination loops when a single
                  // cell is taller than the page content area. PaginationPlus sets
                  // --rm-max-content-child-height on the editor root. Without this
                  // clamp the pagination engine oscillates infinitely.
                  '& .ProseMirror.rm-with-pagination td, & .ProseMirror.rm-with-pagination th': {
                    maxHeight: 'var(--rm-max-content-child-height, none) !important',
                    overflowY: 'auto !important',
                  },
                }
              : {}),
            '@media print': {
              padding: 0,
              backgroundColor: '#ffffff',
              overflow: 'visible',
              '& .ProseMirror.rm-with-pagination': {
                boxShadow: 'none !important',
                border: 'none !important',
                minHeight: 'auto !important',
                // Override inline styles set by PaginationPlus so @page margins
                // are the sole source of page spacing in print.
                width: '100% !important',
                padding: '0 !important',
                margin: '0 !important',
              },

              // Disable PaginationPlus's screen-only pagination widgets in print.
              // The invisible .page floats and visible .breaker decorations both
              // interfere with native browser pagination and @page margins.
              '& .ProseMirror.rm-with-pagination .rm-pages-wrapper, & .ProseMirror.rm-with-pagination .rm-first-page-header':
                {
                  display: 'none !important',
                },
              // --- Phase 2: Content break control ---
              '& .ProseMirror h1, & .ProseMirror h2, & .ProseMirror h3, & .ProseMirror h4, & .ProseMirror h5, & .ProseMirror h6':
                {
                  breakAfter: 'avoid',
                  pageBreakAfter: 'avoid',
                },
              '& .ProseMirror .image-plus-wrapper, & .ProseMirror .document-image-placeholder-node':
                {
                  breakInside: 'avoid',
                  pageBreakInside: 'avoid',
                },
              '& .ProseMirror li': {
                breakInside: 'avoid',
                pageBreakInside: 'avoid',
              },
              '& .ProseMirror li[data-type="taskItem"]': {
                breakInside: 'avoid',
                pageBreakInside: 'avoid',
              },
              '& .ProseMirror p': {
                orphans: 3,
                widows: 3,
              },
              // --- Phase 3: Table print rules ---
              '& .ProseMirror tr': {
                breakInside: 'avoid',
                pageBreakInside: 'avoid',
              },
              '& .ProseMirror td, & .ProseMirror th': {
                maxHeight: 'none !important',
                overflow: 'visible !important',
              },
              // Standard table: reset grid/contents display back to native table
              // layout for print so the browser can handle table pagination.
              ...(useStandardTable
                ? {
                    '& .ProseMirror.rm-with-pagination table': {
                      display: 'table !important',
                      width: '100% !important',
                    },
                    '& .ProseMirror.rm-with-pagination table tbody': {
                      display: 'table-row-group !important',
                      maxHeight: 'none !important',
                      overflowY: 'visible !important',
                    },
                    '& .ProseMirror.rm-with-pagination table tbody > tr, & .ProseMirror.rm-with-pagination table > tr':
                      {
                        display: 'table-row !important',
                      },
                    // Higher specificity to beat the non-print .rm-with-pagination rules
                    '& .ProseMirror.rm-with-pagination td, & .ProseMirror.rm-with-pagination th': {
                      maxHeight: 'none !important',
                      overflow: 'visible !important',
                    },
                  }
                : {}),
              // --- Phase 4: Editor artifact cleanup ---
              '& .ProseMirror .image-resize-handle': {
                display: 'none !important',
              },
              '& .ProseMirror .image-plus-container.image-plus-selected': {
                outline: 'none',
              },
              '& .ProseMirror .column-resize-handle': {
                display: 'none !important',
              },
              '& .ProseMirror .selectedCell::after': {
                display: 'none !important',
              },
            },
          }}
        >
          <EditorContent editor={liveEditor} />
        </Box>
      </Box>
    </Box>
  );
};

function clampZoom(value: number): number {
  if (!Number.isFinite(value)) return 100;
  return Math.round(Math.max(MIN_ZOOM_PERCENT, Math.min(MAX_ZOOM_PERCENT, value)));
}

export default TiptapDocumentCanvas;
